import * as React from "react";
import { useSelector, useDispatch } from "@teamprevent/admin-redux";

import { Button, Card, Grid, Loader, Menu, Segment } from "semantic-ui-react";

import { Detail } from "@neworbit/simpleui-detail";

import { goToParent } from "@teamprevent/common/routing";
import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";

import {
    OccupationalIllness, OccupationalIllnessCategory,
} from "../models";

import { OccupationalIllnessApi } from "../occupationalIllnessApi";

import {
    employeeRecordIdSelector,
    pathSelector,
    basePathSelector,
    occupationalIllnessIdSelector,
    customerIdSelector
} from "../selectors";

import { Edit } from "./Edit";

import { Fragment, Link } from "redux-little-router";

export const Details: React.FunctionComponent = () => {
    const path = useSelector(pathSelector);
    const employeeRecordId = useSelector(employeeRecordIdSelector);
    const customerId = useSelector(customerIdSelector);
    const basePath = useSelector(basePathSelector);
    const dispatch = useDispatch();

    const occupationalIllnessId = useSelector(occupationalIllnessIdSelector);
    const [loading, setLoading] = React.useState(false);
    const [occupationalIllness, setOccupationalIllness] = React.useState<OccupationalIllness | undefined>(undefined);

    React.useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            const occupationalIllnessData = await OccupationalIllnessApi.get(occupationalIllnessId, employeeRecordId);
            setOccupationalIllness(occupationalIllnessData);
        };
        fetchData().then(() => setLoading(false));
    }, [employeeRecordId, occupationalIllnessId]);

    const onClose = React.useCallback(() => dispatch(goToParent()), [dispatch]);

    const onDelete = React.useCallback(async () => {
        await OccupationalIllnessApi.delete(occupationalIllnessId, employeeRecordId);
        onClose();
    },[employeeRecordId, occupationalIllnessId, onClose]);

    const ohCaseUrl = occupationalIllness?.ohCase ?
        `/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${occupationalIllness.ohCase.id}` : "";

    const getDateCompletedOn = () =>  occupationalIllness?.dateModified ?? occupationalIllness?.dateCreated;

    if (loading || !occupationalIllness) {
        return <Loader active />;
    }

    return (
        <>
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column>
                        <h1>{occupationalIllness?.category ? OccupationalIllnessCategory[occupationalIllness?.category] : ""}</h1>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={3} as={Grid} padded>
                        <Grid.Column vertical as={Menu}>
                            <Menu.Item as={Link} href={basePath}>
                                All Occupational Illness Records
                            </Menu.Item>
                        </Grid.Column>
                    </Grid.Column>
                    <Grid.Column width={13}>
                        <Card fluid>
                            <Segment attached>
                                <Grid>
                                    <Detail label="Category" value={occupationalIllness?.category
                                        ? OccupationalIllnessCategory[occupationalIllness?.category]
                                        : ""} />
                                    <Detail label="Linked OH Case" value={
                                        <Link href={ohCaseUrl}>
                                            {occupationalIllness?.ohCase?.name}
                                        </Link>
                                    } />
                                    <Detail label="Injury Or Illness" value={occupationalIllness?.injuryOrIllness} />
                                    <Detail label="Diagnosed Or Suspected" value={occupationalIllness?.diagnosedOrSuspected} />
                                    <Detail label="Illness Started" value={occupationalIllness?.illnessStarted?.format("DD.MM.YYYY") ?? ""} />
                                    <Detail label="OSHA - AstraZeneca Global SHE Reportable" value={occupationalIllness?.oshaReportable ? "Yes": "No"} />
                                    <Detail label="Riddor Reportable" value={occupationalIllness?.riddorReportable ? "Yes" : "No"} />
                                    <Detail label="Reported to the SHE Team" value={occupationalIllness?.reported ? "Yes" : "No"} />
                                    <Detail label="Date Reported to the SHE Team" value={
                                        occupationalIllness?.dateReportedToTheSheTeam?.format("DD.MM.YYYY") ?? ""} />
                                    <Detail label="AstraZeneca Local SHE Reportable" value={occupationalIllness?.astraZenecaLocalSheReportableChange
                                        ? "Yes" : "No"} />
                                    <Detail label="Description" value={occupationalIllness?.description} />
                                    <Detail label="Has Been Absent" value={occupationalIllness?.hasBeenAbsent ? "Yes" : "No"} />
                                    <Detail label="First Day of Absence" value={occupationalIllness?.firstDayOfAbsence?.format("DD.MM.YYYY") ?? ""} />
                                    <Detail label="Last Day of Absence" value={occupationalIllness?.lastDayOfAbsence?.format("DD.MM.YYYY") ?? ""} />
                                    <Detail label="Description of Further Action" value={occupationalIllness?.descriptionOfFurtherAction} />
                                    <Detail label="Completed on" value={getDateCompletedOn()!.format("DD.MM.YYYY") ?? ""} />
                                    <Detail label="Completed by" value={occupationalIllness?.completedByName ?? ""} />
                                </Grid>
                            </Segment>
                            <Segment attached>
                                <ConfirmedButton
                                    icon="delete"
                                    content="Delete"
                                    floated="left"
                                    className="warning"
                                    onClick={onDelete}
                                    promptText="Are you sure you want to delete this occupational illness record? Type 'DELETE' and click confirm to delete."
                                    typeToConfirmText="DELETE"
                                    headingText="Delete Occupational Illness Record"
                                />
                                <Button icon="pencil" content="Edit" floated="right" as={Link} href={`${path}/edit`} />
                            </Segment>
                        </Card>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Fragment forRoute="/edit">
                <Edit occupationalIllness={occupationalIllness} onSaved={setOccupationalIllness} />
            </Fragment>
        </>
    );
};
